import { Filter } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocations } from "../../hooks/useLocations";
import strings from "../../i18n/i18nStrings";
import { WHERE_OPTIONS } from "./filterDropdown.constants";
import { DefaultFilterType } from "../../types/PageFilterTypes";
import { DeviceFilterType } from "../../types/Devices";

const FilterDropdown = ({
  isDeviceSettingsFilter,
  updateFilter,
}: {
  isDeviceSettingsFilter?: boolean;
  updateFilter: (newFilter: DefaultFilterType & DeviceFilterType) => void;
}) => {
  const { locations } = useLocations();

  const [isOpen, setIsOpen] = useState(false);
  const [whereValue, setWhereValue] = useState(0);
  const [isValue, setIsValue] = useState<string | number>(0);

  const componentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (window.location?.search) {
      const queryParams = new URLSearchParams(window.location.search);
      const locationQuery = queryParams.get("location");

      if (locationQuery) {
        setIsOpen(true);
        setWhereValue(1);
        setIsValue(locationQuery);
        updateFilter({ location_id: locationQuery })
      }
    }

    const handleClickOutside = (event: { target: any }) => {
      if (
        componentRef.current &&
        !componentRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getWhereOptions = () => {
    if (isDeviceSettingsFilter) {
      return WHERE_OPTIONS.slice(0, 2);
    }

    return WHERE_OPTIONS;
  };

  const getIsOptions = () => {
    const isOptions: [{
      id: number | string;
      text: string
    }] = [
      {
        id: 0,
        text: strings.common.filter.selectOne,
      },
    ];

    switch (whereValue) {
      case 1:
        const locationOptions = locations?.map((d) => ({
          id: d._id,
          text: d.name,
        }));

        return isOptions.concat(locationOptions);
      case 2:
      case 3:
      case 4:
        const booleanOptions = [
          {
            id: "true",
            text: strings.common.filter.true,
          },
          {
            id: "false",
            text: strings.common.filter.false,
          },
        ];

        return isOptions.concat(booleanOptions);
      default:
        return isOptions;
    }
  };

  const clearFilters = () => {
    updateFilter({
      location_id: null,
      has_battery: null,
      has_water: null,
      is_online: null,
    })
  }

  const handleWhereChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setWhereValue(parseInt(event.target.value));
    setIsValue(0);
    clearFilters();
  };

  const handleIsChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newIsValue = event.target.value;
    setIsValue(newIsValue);

    const option = WHERE_OPTIONS.find(w => w.id === whereValue)
    const selection = getIsOptions().find(g => g.id === newIsValue)

    if (option?.key) {
      updateFilter({ [option.key]: selection?.id })
    }
  };

  const clearFilter = () => {
    setWhereValue(0);
    setIsValue(0);
    clearFilters();
  };

  return (
    <div className="relative" ref={componentRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-semibold text-blue-700 bg-white hover:bg-gray-50"
      >
        <Filter className="mr-2 h-5 w-5 text-blue-700" />
        {whereValue && isValue
          ? strings.common.filter.multiFilter(1)
          : strings.common.filter.title}
      </button>

      {isOpen && (
        <div className="absolute -left-2 bottom-16 mt-2 w-80 md:w-96 bg-white border border-gray-300 rounded-md shadow-lg z-10">
          <div className="p-5">
            <div className="flex space-x-4 mb-4">
              <div className="flex-1">
                <span className="text-sm font-semibold">
                  {strings.common.filter.where}
                </span>
                <div className="p-1"></div>
                <select
                  value={whereValue}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  onChange={handleWhereChange}
                >
                  {getWhereOptions().map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.text}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <span className="text-sm font-semibold">
                  {strings.common.filter.is}
                </span>
                <div className="p-1"></div>
                <select
                  value={isValue}
                  onChange={handleIsChange}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  {getIsOptions().map((option, idx) => (
                    <option key={idx} value={option?.id}>
                      {option?.text}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="text-sm font-semibold flex justify-end">
              <button
                onClick={clearFilter}
                className="text-red-600 hover:text-red-800"
              >
                {strings.common.filter.clear}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterDropdown;
